import React, {
  useEffect,
  useRef,
  useCallback,
  memo
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import IconButton from '../../molecules/icon-button';
import IconCircleArrow from '../../atoms/icon-circle-arrow';

const ButtonWrapper = styled.span`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${({ theme }) => theme.layers.downstage - 1};
  bottom: -100%;
  transition: bottom ${({ theme }) => theme.animation.fast} ${({ theme }) => theme.animation.easeInQuad};

  ${({ active }) => active && css`
    bottom: 0;
  `}

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    position: relative;
    bottom: 0;
    transform: translateX(0);
    left: 0;
    z-index: initial;
  }
`;

const TopButton = styled(IconButton)`
  align-items: center;
  background-color: ${(props) => props.theme.color.background};
  border: 0;
  bottom: 0;
  color: ${(props) => props.theme.color.brand};
  display: inline-flex;
  flex-direction: column;
  left: 0;
  margin-top: 2rem;
  max-height: fit-content;
  outline: 0;
  padding: 1rem 0.5rem 0.5rem 0.5rem;
  width: 6rem;

  svg {
    transform: rotate(-90deg);
    margin: 0;
    stroke: ${(props) => props.theme.color.brand};

    path {
      transform: translate3d(0, 0, 0);
    }
  }

  i {
    margin-top: 0.5rem;
    line-height: 1.5;
  }

  :focus,
  :hover,
  :active {
    svg {
      path {
        animation: arrow-head;
        animation-duration: ${(props) => props.theme.animation.slow};
        animation-timing-function: ${(props) => props.theme.animation.easeMove};
        animation-delay: 0s;
        animation-iteration-count: 1;
        animation-direction: normal;
        animation-fill-mode: forwards;
        animation-play-state: running;
      }
    }
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    background-color: transparent;
    left: initial;
    margin-bottom: 2rem;
    padding: 0;
    position: unset;
  }
`;

const BackToTop = () => {
  const ref = useRef();

  const requestTick = useCallback(() => {
    const { window } = global;

    let ticking = false;

    if (ref.current && !ticking) {
      window.requestAnimationFrame(() => {
        if (window.scrollY > 300) {
          if (ref.current?.style) {
            ref.current.style.bottom = '0';
          }
        } else if (ref.current?.style) {
          ref.current.style.bottom = '-6rem';
        }

        ticking = false;
      });
    }

    ticking = true;
  }, [ref]);

  useEffect(() => {
    global.window.addEventListener('scroll', requestTick, false);
  }, []);

  return (
    <ButtonWrapper ref={ref}>
      <TopButton text="To Top" clicked={() => global.window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <IconCircleArrow strokeWidth={3} />
      </TopButton>
    </ButtonWrapper>
  );
};

export default memo(BackToTop);
